import type { LocalDateTime } from '@nandanam/domain';

/** One user action. Created once in storage, never modified. */
export interface Op {
  v: 1;
  opId: string;
  deviceId: string;
  actor: string;
  /** Author's device time when they acted (IST wall clock). Used as "now" when replaying the command. */
  at: LocalDateTime;
  /** The entity this op changes, e.g. "record:01J…", "customer:01J…", "settings:holdHours". */
  entity: string;
  /** Last op on `entity` the author had seen. Mismatch on replay = concurrent edit. */
  basis: string | null;
  cmd: string;
  args: any;
}

/** What a listing of the ops folder returns. `createdTime` is assigned by the server (OneDrive). */
export interface RemoteOpMeta { opId: string; fileId: string; createdTime: string }

export type AccessLevel = 'editor' | 'viewer' | 'none';

export class RemoteError extends Error {
  constructor(public kind: 'offline' | 'denied' | 'not-found' | 'quota' | 'auth-expired' | 'unknown', message: string) {
    super(message);
    this.name = 'RemoteError';
  }
}

/**
 * The only things sync needs from storage. OneDriveRemote (onedrive.ts) is the real one;
 * tests use MemoryRemote. Implementations must never update or delete op files.
 */
export interface Remote {
  access(): Promise<AccessLevel>;
  /** All ops currently visible, in any order. */
  listOps(): Promise<RemoteOpMeta[]>;
  readOp(meta: RemoteOpMeta): Promise<Op>;
  /**
   * Create the op file. If an op with this opId already exists (an earlier attempt succeeded but its
   * response was lost) return that one instead of creating a second.
   */
  createOp(op: Op): Promise<RemoteOpMeta>;
}
